import React, { useState, useEffect, useRef } from "react"; // Import useState, useEffect, and useRef
import {
  Grid,
  Modal,
  TextField, // Import TextField
  IconButton, // Import IconButton
  Typography, // Import Typography
  Box, // Import Box
  Button,
  Tooltip,
  CircularProgress, // Import Button
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded"; // Import CloseRounded
import AttachFileIcon from "@mui/icons-material/AttachFile"; // Import AttachFileIcon
import CloseIcon from "@mui/icons-material/Close"; // Import CloseIcon
import FormatBoldIcon from "@mui/icons-material/FormatBold"; // Import FormatBoldIcon
import FormatItalicIcon from "@mui/icons-material/FormatItalic"; // Import FormatItalicIcon
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft"; // Import FormatAlignLeftIcon
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter"; // Import FormatAlignCenterIcon
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight"; // Import FormatAlignRightIcon
import FormatClearIcon from "@mui/icons-material/FormatClear"; // Import FormatClearIcon

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography"; // Import MDTypography
import "./css/send_email.css";
import "../users.css";
import { useDispatch } from "react-redux";
import { SendEmailModal } from "api-services";

// Custom Toolbar Component
const TextToolbar = ({
  isBold,
  isItalic,
  textAlign,
  hasAttachment, // Add new prop
  onBold,
  onItalic,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onClearFormatting,
  onAttachFile,
}) => {
  // Create a ref for the file input
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      onAttachFile(e);
    }
    // Always reset the input value to allow selecting the same file again
    fileInputRef.current.value = "";
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        marginBottom: 1,
        background: "#353c4f",
      }}
    >
      <IconButton
        onClick={onBold}
        className={isBold ? "green-cls" : "white"}
        color={isBold ? "" : "white"}
      >
        <FormatBoldIcon />
      </IconButton>
      <IconButton
        onClick={onItalic}
        className={isItalic ? "green-cls" : "white"}
        color={isItalic ? "" : "white"}
      >
        <FormatItalicIcon />
      </IconButton>
      <IconButton
        onClick={onAlignLeft}
        className={textAlign === "left" ? "green-cls" : "white"}
        color={textAlign === "left" ? "" : "white"}
      >
        <FormatAlignLeftIcon />
      </IconButton>
      <IconButton
        onClick={onAlignCenter}
        className={textAlign === "center" ? "green-cls" : "white"}
        color={textAlign === "center" ? "" : "white"}
      >
        <FormatAlignCenterIcon />
      </IconButton>
      <IconButton
        onClick={onAlignRight}
        className={textAlign === "right" ? "green-cls" : "white"}
        color={textAlign === "right" ? "" : "white"}
      >
        <FormatAlignRightIcon />
      </IconButton>
      <IconButton onClick={onClearFormatting} color="white">
        <FormatClearIcon /> {/* Clear formatting */}
      </IconButton>
      <Tooltip
        title="Max file size: 10MB
        File Formats: JPG, PNG, Docx, JSON, ZIP"
        placement="right"
        className="custom-tooltip"
      >
        <span>
          {" "}
          {/* Wrap in span to allow tooltips on disabled buttons */}
          <IconButton
            className={hasAttachment ? "green-cls" : "white-cls"}
            component="label"
            sx={{ marginLeft: 1 }}
          >
            <AttachFileIcon />
            <input
              ref={fileInputRef}
              hidden
              accept="JPG, PNG, Docx, JSON, ZIP"
              type="file"
              onChange={handleFileChange}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

function SendEmail({
  open,
  handleClose,
  darkMode,
  style,
  selectedItem,
  isProduction,
}) {
  const dispatch = useDispatch();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [sendStatus, setSendStatus] = useState({ success: false, error: null });
  const [isSending, setIsSending] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Text formatting states
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [textAlign, setTextAlign] = useState("left");
  const contentEditableRef = useRef(null); // Reference for contentEditable div

  useEffect(() => {
    if (open && selectedItem) {
      setTo(selectedItem.email);
    }
  }, [open, selectedItem]);

  useEffect(() => {
    // Add input handler for contentEditable div
    const contentDiv = contentEditableRef.current;
    if (contentDiv) {
      const handleInput = () => {
        setIsEmpty(!contentDiv.textContent.trim());
      };

      contentDiv.addEventListener("input", handleInput);
      return () => contentDiv.removeEventListener("input", handleInput);
    }
  }, []);

  const handleAttachFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      // console.log("Attached file:", file); // Debug log
      setFile(file);
    }
  };

  const handleRemoveAttachment = () => {
    setFile(null);
  };

  const handleValidation = () => {
    let tempErrors = {};
    let isValid = true;

    // Subject validation
    if (!subject?.trim()) {
      tempErrors["subject"] = true;
      isValid = false;
    }

    if (subject?.length > 100) {
      tempErrors["subject_length"] = true;
      isValid = false;
    }

    // Message validation
    const messageContent = contentEditableRef.current?.innerHTML;
    const strippedMessage = messageContent?.replace(/<[^>]*>/g, "").trim(); // Remove HTML tags and trim

    if (!strippedMessage) {
      tempErrors["message"] = true;
      isValid = false;
    }

    if (messageContent?.length > 1000) {
      tempErrors["message_length"] = true;
      isValid = false;
    }

    // File validation
    if (file) {
      if (file.size > 10485760) {
        tempErrors["file_size"] = true;
        isValid = false;
      }

      const validTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/zip",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/json",
      ];

      if (!validTypes.includes(file.type)) {
        tempErrors["file_type"] = true;
        isValid = false;
      }
    }

    setErrors(tempErrors);
    return isValid;
  };

  const encodeHTML = (html) => {
    return html
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const applyFormatting = (format) => {
    document.execCommand(format); // Use execCommand to apply formatting
    if (format === "bold") {
      setIsBold(!isBold);
    } else if (format === "italic") {
      setIsItalic(!isItalic);
    }
  };

  const cleanText = (text) => {
    return text
      .split(/\n/)
      .map((line) => {
        // Convert non-breaking spaces at start/end to regular spaces
        const normalizedLine = line.replace(
          /^(&nbsp;|\s)+|(&nbsp;|\s)+$/g,
          " "
        );

        // Trim only leading and trailing spaces
        return normalizedLine.trim();
      })
      .join("\n");
  };

  const getEncodedMessage = () => {
    const htmlContent = contentEditableRef.current.innerHTML;

    // Process the content
    const processedContent = htmlContent
      .split(/(<[^>]*>)/)
      .map((part) => {
        // If it's an HTML tag, leave it unchanged
        if (part.startsWith("<")) {
          return part;
        }

        // Clean text content - only trim start/end
        return cleanText(part);
      })
      .filter((part) => part.length > 0)
      .join("");

    return `&lt;div style="text-align: ${textAlign}"&gt;${encodeHTML(
      processedContent
    )}&lt;/div&gt;`;
  };

  const handleSend = async () => {
    if (!handleValidation()) return;

    setIsSending(true);
    try {
      const data = new FormData();
      data.append("EmailTo", to);
      data.append("Subject", subject);
      data.append("Message", getEncodedMessage());
      if (file) {
        data.append("file", file);
      }
      data.append("isProduction", isProduction);
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      SendEmailModal(
        dispatch,
        data,
        config,
        () => {
          // Success callback
          setSendStatus({ success: true, error: null });
          setIsSending(false); // Stop loading on success
          setTimeout(() => {
            handleModalClose();
          }, 5000);
        },
        (errorMessage) => {
          // Error callback
          setSendStatus({ success: false, error: errorMessage });
          setIsSending(false); // Stop loading on error
        },
        () => {
          // Clear fields callback
          setTo("");
          setSubject("");
          contentEditableRef.current.innerHTML = "";
          setFile(null);
        }
      );
    } catch (error) {
      setSendStatus({ success: false, error: "Failed to send email" });
      setIsSending(false); // Stop loading on error
    }
  };

  // Add status message component
  const StatusMessage = () => {
    if (!sendStatus.success && !sendStatus.error) return null;

    return (
      <Box sx={{ mt: 2, textAlign: "center" }}>
        {sendStatus.success ? (
          <MDTypography color="success" variant="subtitle2">
            Email sent successfully!
          </MDTypography>
        ) : (
          <MDTypography color="error" variant="subtitle2">
            {typeof sendStatus.error === "object"
              ? Object.values(sendStatus.error).join(", ")
              : sendStatus.error}
          </MDTypography>
        )}
      </Box>
    );
  };

  const handleModalClose = () => {
    // Reset form fields
    setTo("");
    setSubject("");
    setFile(null);
    contentEditableRef.current.innerHTML = ""; // Clear contentEditable

    // Reset formatting
    setIsBold(false);
    setIsItalic(false);
    setTextAlign("left");

    // Reset errors
    setErrors({});

    setSendStatus({ success: false, error: null });
    setIsSending(false);
    handleClose();
  };

  const clearFormatting = () => {
    setIsBold(false);
    setIsItalic(false);
    setTextAlign("left");
    contentEditableRef.current.innerHTML = ""; // Optionally clear the message as well
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="sendEmail"
      aria-describedby="sendEmail"
      className="responsive-modal send-email"
    >
      <div className={darkMode ? "darkmode_wrap" : "lightmode_wrap"}>
        <MDBox sx={style.modal}>
          <MDBox onClick={handleModalClose} className="close-modal-sm">
            <CloseRounded color={darkMode ? "white" : "secondary"} />
          </MDBox>
          <MDTypography variant="h5" sx={{ textTransform: "Capitalize" }}>
            Send Email
          </MDTypography>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} lg={6}>
              <MDBox className="mb-3">
                <TextField
                  className="to"
                  label="To"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={to}
                  InputLabelProps={{ style: { color: "#fff" } }}
                  InputProps={{
                    style: {
                      color: "#fff",
                      background: "#343C50",
                    },
                  }}
                  disabled
                  required
                />
                <TextField
                  label="Subject"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  InputLabelProps={{ style: { color: "#fff" } }}
                  error={errors.subject || errors.subject_length}
                  helperText={
                    errors.subject
                      ? "Subject is required"
                      : errors.subject_length
                      ? "Subject must be less than 100 characters"
                      : ""
                  }
                  required
                />
              </MDBox>
            </Grid>
          </Grid>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} lg={12}>
              <TextToolbar
                isBold={isBold}
                isItalic={isItalic}
                textAlign={textAlign}
                hasAttachment={file !== null} // Pass file state
                onBold={() => applyFormatting("bold")}
                onItalic={() => applyFormatting("italic")}
                onAlignLeft={() => setTextAlign("left")}
                onAlignCenter={() => setTextAlign("center")}
                onAlignRight={() => setTextAlign("right")}
                onClearFormatting={clearFormatting}
                onAttachFile={handleAttachFile} // Pass the handler
              />
              <div style={{ position: "relative" }}>
                <div
                  ref={contentEditableRef}
                  contentEditable
                  onFocus={() => setIsEmpty(false)}
                  onBlur={() =>
                    setIsEmpty(!contentEditableRef.current.textContent.trim())
                  }
                  style={{
                    border: errors.message
                      ? "1px solid #f44336"
                      : "1px solid #8B8B8B",
                    padding: "10px",
                    minHeight: "120px",
                    maxHeight: "120px",
                    overflowY: "auto",
                    borderRadius: "4px",
                    textAlign: textAlign,
                    backgroundColor: darkMode ? "" : "",
                    color: darkMode ? "#fff" : "#000",
                    fontSize: "14px",
                  }}
                />
                {isEmpty && (
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      color: "#ccc",
                      pointerEvents: "none",
                      fontSize: "14px",
                    }}
                  >
                    Enter message...
                  </div>
                )}
              </div>
              {errors.message && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block" }}
                >
                  Message is required
                </Typography>
              )}
              {errors.message_length && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block" }}
                >
                  Message must be less than 1000 characters
                </Typography>
              )}
              {/* Move Box outside TextField but keep attachments display */}
              {(file || errors.file_size || errors.file_type) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    margin: "10px 0",
                  }}
                >
                  {file && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginRight: 2,
                          marginBottom: 1,
                        }}
                        className="send-attchement"
                      >
                        <Typography variant="body2" sx={{ color: "#fff" }}>
                          {file.name}
                        </Typography>
                        <IconButton
                          color="secondary"
                          onClick={() => handleRemoveAttachment()}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                  {(errors.file_size || errors.file_type) && (
                    <Box sx={{ mt: 1 }}>
                      {errors.file_size && (
                        <Typography
                          color="error"
                          variant="caption"
                          display="block"
                        >
                          File size must be less than 10MB
                        </Typography>
                      )}
                      {errors.file_type && (
                        <Typography
                          color="error"
                          variant="caption"
                          display="block"
                        >
                          Invalid file type. Allowed types: PNG, JPG, DOCX,
                          JSON, ZIP
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              <StatusMessage />
              <Button
                variant="text"
                color="success"
                size="small"
                onClick={handleSend}
                disabled={isSending}
                className="btn-update mt-3 px-2 py-3"
              >
                {isSending ? (
                  <CircularProgress size={25} color="white" />
                ) : (
                  "Send"
                )}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </div>
    </Modal>
  );
}

export default SendEmail;
